import { Shield, Lock, Eye, Mail } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-[#003366] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto bg-[#FFD700]/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-[#FFD700]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-slate-300">Last updated: March 20, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              IPVALUE MANAGEMENTS ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website and use our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">2. Information We Collect</h2>
            
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">Personal Information</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4 mb-4">
              <li>Name and contact information (email address, shipping address)</li>
              <li>Payment information (processed securely through Square)</li>
              <li>Account credentials</li>
              <li>Order history and preferences</li>
              <li>Communications with our customer service team</li>
            </ul>

            <h3 className="font-semibold text-slate-800 mt-4 mb-2">Automatically Collected Information</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you visit our website, we automatically collect:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Device information (browser type, operating system)</li>
              <li>IP address and general location</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website or source</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Process and fulfill your orders</li>
              <li>Send order confirmations and shipping updates</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our website and services</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Prevent fraud and enhance security</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">4. Information Sharing</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Third parties who help us operate our business (payment processors, shipping carriers, etc.)</li>
              <li><strong>Business Partners:</strong> With your consent, for joint marketing efforts</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">5. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. This includes 
              encryption, secure servers, and regular security assessments. However, no method of transmission 
              over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">6. Cookies and Tracking</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Remember your preferences and cart contents</li>
              <li>Understand how you use our website</li>
              <li>Provide personalized content and ads</li>
              <li>Analyze site traffic and trends</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              You can control cookies through your browser settings, though disabling them may affect 
              site functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">7. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our services are not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If we learn we have collected 
              information from a child under 13, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">9. Age-Restricted Products</h2>
            <p className="text-slate-600 leading-relaxed">
              For customers purchasing age-restricted products, we may collect additional age verification 
              information. We offer discreet packaging options to protect your privacy. Order details for 
              such products are handled with additional confidentiality measures.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">10. Third-Party Links</h2>
            <p className="text-slate-600 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the 
              privacy practices of these external sites. We encourage you to read their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">11. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes for 
              which it was collected, comply with legal obligations, resolve disputes, and enforce our 
              agreements. Order records are typically retained for 7 years for tax and legal purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">12. Changes to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by posting the new policy on our website and updating the "Last updated" date. 
              Your continued use of our services after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#003366] mb-4">13. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-slate-700 font-medium">IPVALUE MANAGEMENTS</p>
              <div className="flex items-center gap-2 text-[#003366]">
                <Mail className="w-5 h-5" />
                <a href="mailto:support@ipvaluemanagements.com" className="hover:underline">
                  support@ipvaluemanagements.com
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
